import { compose, defaultProps, withHooks } from "enhancers";
import {
  Checkbox,
  IconButton,
  Box,
  Chip,
  Typography,
  Avatar,
} from "components";
import { formatPhoneNumber, stripHtmlInline, toCurrency } from "utils/helper";
import { map, find, isEmpty, isNumber } from "lodash";
import { format, parseISO } from "date-fns";
import { DataGrid, GridToolbar, GridOverlay } from "@material-ui/data-grid";

import { Tooltip } from "@material-ui/core";
import MuiSelect from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import "./Table.css";

import { ReactComponent as FacebookIcon } from "assets/icon/facebook.svg";
import { ReactComponent as InstagramIcon } from "assets/icon/instagram.svg";
import { ReactComponent as LineIcon } from "assets/icon/line.svg";
import { ReactComponent as EmailIcon } from "assets/icon/email.svg";

import { ReactComponent as CheckboxOutlineCheckedGreenIcon } from "assets/icon/checkbox_outline_checked_green.svg";
import { ReactComponent as GreenDot } from "assets/icon/green_dot.svg";
import { ReactComponent as RedDot } from "assets/icon/red_dot.svg";

import { ReactComponent as WarningAmber } from "assets/icon/warning_amber.svg";

import { BOOKING_STATUS_TEXT } from "../../constants/index";
import { MaterialIcon } from "./MaterialIcon";
import { AppColor } from "theme/app-color";
import { getCurrentLang } from "common/i18next";
import * as locales from "common/mui";
import T from "./T";
import React from "react";

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BG_COLOR = {
  waiting: "#FF9800",
  confirmed: "#376FD0",
  paid: "#376FD0",
  complete: "#4CAF4F",
  canceled: "#F34336",
};

const DragHandleCell = ({ id }: { id: string | number }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const handleRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (handleRef.current) {
      const rowElement = handleRef.current.closest(".MuiDataGrid-row");
      if (rowElement instanceof HTMLElement) {
        setNodeRef(rowElement);
        rowElement.style.transform = CSS.Transform.toString(transform) || "";
        if (transition) {
          rowElement.style.transition = transition;
        }
        rowElement.style.zIndex = isDragging ? "1000" : "";
        rowElement.style.position = "relative"; // Ensure z-index works
      }
    }
  }, [setNodeRef, transform, transition, isDragging]);

  return (
    <div
      ref={handleRef}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        cursor: "move",
        touchAction: "none",
      }}
      {...attributes}
      {...listeners}
    >
      <MaterialIcon
        name="DragIndicator"
        style={{ fontSize: "18px", color: AppColor["Text/Line"] }}
      />
    </div>
  );
};

const TextOverflowTooltip = ({ value }: { value: any }) => {
  const [isOverflow, setIsOverflow] = React.useState(false);
  const textElementRef = React.useRef<any>(null);

  const checkOverflow = () => {
    const element = textElementRef.current;
    if (element) {
      setIsOverflow(element.scrollWidth > element.clientWidth);
    }
  };

  return (
    <Tooltip title={isOverflow ? value : ""} onMouseEnter={checkOverflow}>
      <div
        ref={textElementRef}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
    </Tooltip>
  );
};

export const RENDER_CELLS = {
  available(row: any) {
    const { value } = row;

    return (
      <Box display="flex" alignItems="center" mx="auto">
        {value ? <GreenDot /> : <RedDot />}
      </Box>
    );
  },
  status(row: any) {
    const value = row.value as keyof typeof BOOKING_STATUS_TEXT;
    if (!value) {
      return <div></div>;
    }
    return (
      <Box whiteSpace="pre-wrap" p={1} pt={0}>
        <Chip
          size="small"
          label={BOOKING_STATUS_TEXT[value]}
          mr={1}
          mt={1}
          style={{
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 14,
            backgroundColor: BG_COLOR[value],
            color: "white",
          }}
        />
      </Box>
    );
  },
  text(row: any) {
    const { value } = row;

    if (!value && value !== 0) {
      return <>-</>;
    }
    return <TextOverflowTooltip value={value} />;
  },
  html(row: any) {
    const { value } = row;

    if (!value) {
      return <>-</>;
    }
    return <>{stripHtmlInline(value)}</>;
  },
  link(row: any) {
    const { value } = row;
    if (isEmpty(value)) {
      return <>-</>;
    }

    return (
      <a href={value} target="_blank" rel="noopener noreferrer">
        <Box
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          style={{
            cursor: "pointer",
            color: AppColor["Other/Info"],
            textDecoration: "underline",
            width: row.colDef.width,
            paddingRight: "8px",
          }}
        >
          {value}
        </Box>
      </a>
    );
  },
  linkOnClick(row: any) {
    const value =
      typeof row.value === "string" ? row.row[row.field] : row.value;

    if (!value || isEmpty(value?.text)) {
      return <>-</>;
    }

    const { text, onClick } = value;

    return (
      <Box
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        onClick={() => onClick(row)}
        style={{
          cursor: "pointer",
          color: AppColor["Other/Info"],
          textDecoration: "underline",
        }}
      >
        {text}
      </Box>
    );
  },
  textTooltip(row: any) {
    const { value } = row;
    if (!value && value !== 0) {
      return <></>;
    }
    return (
      <Tooltip title={value}>
        <Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {value}
        </Box>
      </Tooltip>
    );
  },
  textEnd(row: any) {
    const { value } = row;
    if (!value && value !== 0) {
      return <></>;
    }
    return (
      <Box
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        marginLeft="auto"
      >
        {value}
      </Box>
    );
  },
  checkBox(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    const { statusSent, onClick, disabled } = value;
    return (
      <Box mx="auto">
        <Checkbox
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            onClick(row);
          }}
          checked={statusSent}
          disabled={disabled}
          checkedIcon={<CheckboxOutlineCheckedGreenIcon />}
        ></Checkbox>
      </Box>
    );
  },
  isDefault(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return (
      <Box mx="auto" mt="4px">
        <MaterialIcon
          name="Check"
          style={{ fontSize: "16px", color: AppColor["Secondary/Hover"] }}
        />
      </Box>
    );
  },
  caption(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return <Typography variant="caption">{value}</Typography>;
  },
  phoneNumber(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return formatPhoneNumber(value);
  },
  date(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return value;
  },
  currency(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return (
      <Box>
        {toCurrency(value, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}
      </Box>
    );
  },

  social(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }

    const [info, arr2] = value.split("{{STUDATA_SPLIT_STRING}}");
    const channel: keyof typeof SocialIcon = arr2 as keyof typeof SocialIcon;

    if (!info && !channel) {
      return <></>;
    }

    const SocialIcon = {
      facebook: FacebookIcon,
      ig: InstagramIcon,
      line: LineIcon,
      email: EmailIcon,
    };

    const Icon = SocialIcon[channel];

    return (
      <Box display="flex" alignItems="center">
        {Icon && <Icon style={{ marginRight: 8 }} />}
        {info}
      </Box>
    );
  },
  selector(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    return find(row.colDef.options, { value })?.label;
  },
  tags(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }
    const values = value.split(",");
    return (
      <Box whiteSpace="pre-wrap" p={1} pt={0}>
        {map(values, (v) => {
          return (
            <Chip
              size="small"
              label={v}
              mr={1}
              mt={1}
              style={{ fontSize: 12, lineHeight: 14 }}
            />
          );
        })}{" "}
      </Box>
    );
  },
  tag(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }

    const backgroundColor = ["ใช้งาน", "active"].includes(value)
      ? "#4CAF4F"
      : "#F34336";

    return (
      <Chip
        size="small"
        label={value}
        mr={1}
        style={{
          fontSize: 12,
          lineHeight: 14,
          color: "white",
          backgroundColor: backgroundColor,
        }}
      />
    );
  },
  notiStatus(row: any) {
    const { value } = row;
    if (!value && value !== false) {
      return <></>;
    }

    return (
      <Box display="flex" alignItems="center" mx="auto">
        {value ? <GreenDot /> : <RedDot />}
      </Box>
    );
  },
  warningStatus(row: any) {
    const { value } = row;
    if (!value && value !== false) {
      return <></>;
    }

    return (
      <Box display="flex" alignItems="center" mx="auto">
        {value ? <WarningAmber /> : null}
      </Box>
    );
  },
  textColor(row: any) {
    const { value: rowValue } = row;
    const { value, color } = rowValue;

    if (!value && value !== false) {
      return <></>;
    }

    return (
      <Box display="flex">
        <Typography color={color}>{value}</Typography>
      </Box>
    );
  },
  dropdown(row: any) {
    const { formattedValue } = row;

    const {
      options = [],
      onChange,
      defaultValue,
      width = "100%",
      disabled = false,
    } = formattedValue;

    if (!options) {
      return <></>;
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        height="36px"
        width="100%"
        padding={1}
      >
        <MuiSelect
          variant="outlined"
          style={{ width }}
          SelectDisplayProps={{
            style: { padding: "6px 32px 6px 12px" },
          }}
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) return;
            onChange(e.target.value);
          }}
        >
          {options.map((option: { label: string; value: any }) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
      </Box>
    );
  },
  actions(row: any) {
    const { value } = row;
    if (!value) {
      return <></>;
    }

    return value.map((action: any, index: number) => {
      const { Icon, onClick, can, deletedable, disabled, tooltipText } = action;
      if (can === false) {
        return <></>;
      }

      const defaultStyle = {
        padding: 0,
      };

      const isDeletedableMode = deletedable != null;
      const shouldDisable = isDeletedableMode ? !deletedable : disabled;

      const response = (
        <IconButton
          key={index}
          style={{
            ...(isDeletedableMode && {
              cursor: deletedable ? "pointer" : "default",
            }),
            ...defaultStyle,
            marginLeft: "16px",
          }}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            if (isDeletedableMode) {
              deletedable && onClick(row);
            } else {
              onClick(row);
            }
          }}
          disabled={shouldDisable}
        >
          <Box
            style={{
              display: "inline-flex",
              ...(shouldDisable && {
                opacity: 0.4,
                filter: "grayscale(100%)",
              }),
            }}
          >
            <Icon />
          </Box>
        </IconButton>
      );

      return isEmpty(tooltipText) ? (
        response
      ) : (
        <Tooltip title={tooltipText}>{response}</Tooltip>
      );
    });
  },
  logo(row: any) {
    const { value } = row;
    if (!value || !value.url) {
      return <></>;
    }
    return (
      <Tooltip title={value.companyName}>
        <Avatar
          alt={value.fileName}
          src={value.url}
          style={{ width: "25px", height: "25px" }}
        />
      </Tooltip>
    );
  },
};

const customValue = {
  dateOnly(row: any) {
    const { value } = row;
    if (!value) {
      return null;
    }
    return format(parseISO(value), "dd/MM/yyyy");
  },
  date(row: any) {
    const { value } = row;
    if (!value) {
      return null;
    }
    return format(parseISO(value), "dd/MM/yyyy, HH:mm");
  },
  social(row: any) {
    const { value } = row;

    if (!value.channel || !value.info) {
      return null;
    }

    const { channel, info } = value;

    return `${info}{{STUDATA_SPLIT_STRING}}${channel}`;
  },
  tags(row: any) {
    const { value } = row;
    if (!value) {
      return null;
    }

    const labels = map(
      value,
      (v) => find(row.colDef.options, { value: v })?.label ?? ""
    ).join(",");

    return labels;
  },
};

//https://github.com/mui-org/material-ui-x/blob/HEAD/packages/grid/_modules_/grid/constants/localeTextConstants.ts
//https://material-ui.com/components/data-grid/localization/
// components={{
//   Toolbar: GridToolbar,
// }}

const enhancer = compose(
  defaultProps({
    style: {
      // minHeight: 606,
    },
    density: "compact",
    autoHeight: true,
    autoPageSize: true,
    disableSelectionOnClick: true,
    rowsPerPageOptions: [25, 50, 100],
  }),
  withHooks((props: any, hooks: any) => {
    const {
      columns,
      onRowClickTo,
      onRowClick,
      autoDetectInitialFilterOnUrlQueryParams,
      isFilter = false,
      noRowText,
      onPaginationChange = () => ({}),
      hasNoRowUnderline = false,
      draggable,
      setDataSource,
      rows,
      onDragSort,
      autoHeight,
      autoPageSize,
      fitContent,
      fitContentMaxHeight,
      style,
      ...rest
    } = props;
    const { useMemo, useCallback, useState, useUrlParam, useEffect } = hooks;
    const [pageSize, setPageSize] = useState(
      rest.pageSize || rest.rowsPerPageOptions?.[0] || 999
    );
    const [page, setPage] = useState(0);
    const resolvedRows = rows || props.dataSource || [];

    // fitContent uses autoHeight=false so MUI reads the wrapper div's height
    // and handles internal scrolling natively — no calculation needed.
    const shouldUseAutoHeight = useMemo(() => {
      if (fitContent) return false;
      if (autoHeight === false) return false;
      return autoHeight;
    }, [autoHeight, fitContent]);

    const dataGridStyle = useMemo(() => {
      if (fitContent) {
        // Fill the wrapper div — wrapper controls the height.
        return { height: "100%", ...style };
      }
      if (shouldUseAutoHeight) {
        return style;
      }
      return {
        height: "calc(100vh - 360px)",
        ...style,
      };
    }, [shouldUseAutoHeight, fitContent, style]);

    const customColumns = useMemo(() => {
      // ซ่อน actions column อัตโนมัติเมื่อทุก row มี actions เป็น [] (เช่น ไม่มี permission)
      const actionColumnFields = columns
        .filter((col: any) => col?.type === "actions")
        .map((col: any) => col.field);
      const hasAnyActions = resolvedRows.some((row: any) =>
        actionColumnFields.some(
          (field: string) => Array.isArray(row[field]) && row[field].length > 0
        )
      );

      const actionColumns: any[] = [];
      const nonActionColumns: any[] = [];

      map(columns, (column) => {
        if (column?.type === "actions") {
          if (hasAnyActions) actionColumns.push(column);
          return;
        }
        nonActionColumns.push(column);
      });
      // เรียงคอลัมน์ตามลำดับที่กำหนด ปุ่มก่อน คอลัมน์อื่นๆ
      const orderedColumns = [...actionColumns, ...nonActionColumns];

      return map(orderedColumns, ({ type = "text", ...rest }) => {
        if (draggable && type === "sort") {
          return {
            renderCell: (params: any) => <DragHandleCell id={params.id} />,
            ...rest,
          };
        }
        return {
          renderCell:
            RENDER_CELLS[type as keyof typeof RENDER_CELLS] || undefined,
          valueGetter:
            customValue[type as keyof typeof customValue] || undefined,
          ...rest,
        };
      });
    }, [columns, draggable, resolvedRows]);

    const customOnRowClick = useCallback(
      (row: any) => {
        if (onRowClickTo) {
          return onRowClickTo(row.id).push();
        }
        if (onRowClick) {
          return onRowClick(row);
        }
      },
      [onRowClickTo, onRowClick]
    );

    const onPageSizeChange = useCallback(
      (newPage: number) => setPageSize(newPage),
      []
    );

    const onPageChange = useCallback((newPage: number) => setPage(newPage), []);

    const urlParams = useUrlParam();
    const initialFilterModel = useMemo(() => {
      const items = map(urlParams, (value, key) => {
        return {
          id: key,
          columnField: key,
          operatorValue: "contains",
          value: value,
        };
      });

      return isEmpty(items) ? undefined : { items };
    }, [urlParams]);

    const [filterModel, onFilterModelChange] = useState(initialFilterModel);
    const lang = getCurrentLang() as "th" | "en";
    const localeText = useMemo(() => locales[lang].filter, [lang]);

    const NoRowsOverlay = useCallback(() => {
      const noDataText = isFilter
        ? "client.components.Table.noDataOnFilter"
        : "client.components.Table.noData";
      return (
        <GridOverlay
          style={{
            background: "none",
            height: "120px",
            padding: "0px",
          }}
        >
          <Box
            style={{
              borderBottom: hasNoRowUnderline
                ? `1px solid ${AppColor["Background/Dark Grey"]}`
                : "none",
              width: "100%",
              textAlign: "center",
              paddingBottom: hasNoRowUnderline ? "8px" : "none",
            }}
          >
            <T variant="body1" color={AppColor["Text/Dark Grey"]}>
              {noRowText ? noRowText : noDataText}
            </T>
          </Box>
        </GridOverlay>
      );
    }, [hasNoRowUnderline, isFilter, noRowText]);

    useEffect(() => {
      onPaginationChange(page, pageSize);
    }, [onPaginationChange, page, pageSize]);

    // Draggable Logic
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 1,
        },
      })
    );

    const handleDragEnd = useCallback(
      ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id && onDragSort) {
          onDragSort({ active, over });
        }
      },
      [onDragSort]
    );

    const draggableComponents = useMemo(
      () => ({
        Toolbar: props.includeToolbar ? GridToolbar : undefined,
        NoRowsOverlay,
      }),
      [props.includeToolbar, NoRowsOverlay]
    );

    // Resolve the max height for fitContent mode. Default is 250px.
    const resolvedFitContentMaxHeight = fitContent
      ? typeof fitContentMaxHeight === "number"
        ? fitContentMaxHeight
        : 250
      : undefined;

    return {
      page,
      onPageChange,
      // fitContent with no pagination: show as many rows as possible (MUI free limit: 100).
      pageSize:
        fitContent && (rest.hideFooter || rest.hideFooterPagination)
          ? 100
          : pageSize,
      onPageSizeChange,
      rows: resolvedRows, // Support both rows and dataSource to be safe
      ...rest,
      autoHeight: shouldUseAutoHeight,
      autoPageSize: fitContent
        ? false
        : shouldUseAutoHeight
        ? autoPageSize
        : false,
      style: dataGridStyle,
      columns: customColumns,
      onRowClick: customOnRowClick,
      filterModel,
      onFilterModelChange,
      localeText,
      components: draggableComponents,
      draggable,
      handleDragEnd,
      sensors,
      fitContent,
      fitContentMaxHeight: resolvedFitContentMaxHeight,
    };
  })
);

const CustomTable = enhancer((props: any) => {
  const {
    draggable,
    handleDragEnd,
    sensors,
    rows,
    fitContent,
    fitContentMaxHeight,
    ...rest
  } = props;

  const dataGrid = draggable ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={rows.map((r: any) => r.id)}
        strategy={verticalListSortingStrategy}
      >
        <DataGrid rows={rows} {...rest} />
      </SortableContext>
    </DndContext>
  ) : (
    <DataGrid rows={rows} {...rest} />
  );

  return (
    <div
      id="flex-table"
      // When fitContent, the wrapper has a fixed height so MUI DataGrid
      // (autoHeight=false) reads it via ResizeObserver and handles internal scroll.
      style={fitContent ? { height: fitContentMaxHeight } : undefined}
    >
      {dataGrid}
    </div>
  );
});

CustomTable.RENDER_CELLS = RENDER_CELLS;
export default CustomTable;
